import { supabase } from '../../config/supabase';

/**
 * Track a delivery using the public short ID (no authentication required)
 * NOW RETURNS QUOTE-BASED DELIVERY INFORMATION
 * Shows separate delivery info for each quote type
 * @param {string} shortId - The short tracking ID
 * @returns {Promise<Object>} Tracking information with quote-based deliveries and photos
 */
export const trackDelivery = async (shortId) => {
  try {
    // Get job data using short_id
    const { data: jobData, error: jobError } = await supabase
      .from('jobs')
      .select(`
        id,
        customer_name,
        customer_number,
        customer_address,
        unique_id,
        companies!inner(
          name
        )
      `)
      .eq('short_id', shortId.toUpperCase())
      .single();

    if (jobError) throw jobError;

    // Get all deliveries for this job
    const { data: deliveries, error: deliveryError } = await supabase
      .from('job_deliveries')
      .select('*')
      .eq('job_id', jobData.id)
      .order('delivery_date', { ascending: true });

    if (deliveryError) throw deliveryError;

    if (!deliveries || deliveries.length === 0) {
      throw new Error('No delivery information found');
    }

    // Get quotes to link with deliveries
    const { data: quotes, error: quotesError } = await supabase
      .from('job_quotes')
      .select('id, quote_type, expected_delivery_date, delivery_count, child_unique_id')
      .eq('job_id', jobData.id);

    if (quotesError) console.error('Error fetching quotes:', quotesError);

    // Get ALL delivery photos for this job from job_files table
    const { data: photos, error: photosError } = await supabase
      .from('job_files')
      .select('*')
      .eq('job_id', jobData.id)
      .eq('file_type', 'delivery')
      .order('uploaded_at', { ascending: false});


    if (photosError) {
      console.error('Error fetching photos:', photosError);
    }

    // Group deliveries by quote type
    const quoteDeliveries = deliveries.map(delivery => {
      const deliveryCount = delivery.delivery_count ?? 0;

      // Detect system type: new-system rows carry a child_unique_id (UUID of the job_quotes row)
      const isNewSystem = !!delivery.child_unique_id;

      // Match the corresponding quote
      let quote;
      if (isNewSystem) {
        // New system: delivery.child_unique_id is the UUID of the job_quotes row
        quote = quotes?.find(q => q.id === delivery.child_unique_id);
      } else {
        // Old system: match by quote_type + delivery_count
        quote = quotes?.find(q =>
          q.quote_type === delivery.quote_type &&
          (q.delivery_count ?? 0) === deliveryCount
        );
      }

      const quotePhotos = photos?.filter(p => {
        // New system: prefer matching by child_unique_id when both sides have it
        if (isNewSystem && delivery.child_unique_id && p.child_unique_id) {
          return p.child_unique_id === delivery.child_unique_id;
        }
        // Old system (and new-system fallback): match by quote_type + delivery_count
        if (p.quote_type !== delivery.quote_type) return false;
        // Handle backwards compatibility for delivery_count
        if (deliveryCount === 0) {
          return p.delivery_count === 0 || p.delivery_count === null || p.delivery_count === undefined;
        }
        return p.delivery_count === deliveryCount;
      }) || [];


      return {
        quote_type: delivery.quote_type,
        child_unique_id: delivery.child_unique_id || null,
        // child_display_name = the human-readable label from job_quotes (e.g. "W1", "W2")
        // delivery.child_unique_id is a UUID; the display string lives on quote.child_unique_id
        child_display_name: quote?.child_unique_id || null,
        delivery_status: delivery.delivery_status,
        delivery_date: delivery.delivery_date,
        expected_delivery_date: quote?.expected_delivery_date,
        delivery_address: delivery.delivery_address,
        assigned_driver_name: delivery.assigned_driver_name,
        delivery_notes: delivery.delivery_notes,
        delivered_at: delivery.delivered_at,
        delivery_count: delivery.delivery_count,
        photos: quotePhotos
      };
    });

    // Calculate delivery statistics
    const totalDeliveries = quoteDeliveries.length;
    const deliveredCount = quoteDeliveries.filter(d => d.delivery_status === 'delivered').length;
    const inTransitCount = quoteDeliveries.filter(d => d.delivery_status === 'in_transit').length;
    const scheduledCount = quoteDeliveries.filter(d => d.delivery_status === 'scheduled').length;

    // Determine overall status based on delivery progress
    let overall_status;
    if (deliveredCount === totalDeliveries) {
      overall_status = 'delivered';
    } else if (deliveredCount > 0 || inTransitCount > 0) {
      overall_status = 'in_progress';
    } else if (scheduledCount > 0) {
      overall_status = 'scheduled';
    } else {
      overall_status = 'not_scheduled';
    }

    // Format the response with main job info + quote deliveries
    const trackingData = {
      // Main job information
      short_id: shortId.toUpperCase(),
      company_name: jobData.companies.name,
      customer_name: jobData.customer_name,
      customer_address: jobData.customer_address,

      // Quote-based deliveries
      deliveries: quoteDeliveries,

      // Overall status and progress
      overall_status: overall_status,
      total_deliveries: totalDeliveries,
      delivered_count: deliveredCount,
      in_transit_count: inTransitCount,
      scheduled_count: scheduledCount
    };

    return { data: trackingData, error: null };
  } catch (error) {
    console.error('Error tracking delivery:', error);
    return { data: null, error };
  }
};